import React from "react";
import PropTypes from "prop-types";
import { SignOut } from "@phosphor-icons/react";
import {
  ProfilePopoverContent,
  ProfilePopoverHeader,
  ProfilePopoverAvatar,
  ProfilePopoverIdentity,
  ProfileName,
  ProfileEmail,
  ProfileRoleBadge,
  ProfileDivider,
  ProfileMeta,
  ProfileActionButton,
} from "./PrimarySidebarStyled";

// Derive up-to-two-letter initials from the best name-ish field we have.
// Order of preference: first+last, first, last, username, email local-part.
export const getUserInitials = (user) => {
  if (!user) return "?";
  const first = (user.first_name || "").trim();
  const last = (user.last_name || "").trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (last) return last.slice(0, 2).toUpperCase();
  if (user.username) return user.username.slice(0, 2).toUpperCase();
  if (user.email) {
    // Use the email local-part ("alice" out of "alice@acme.com") so we don't
    // render "A@" for short emails.
    const localPart = user.email.split("@")[0] || "";
    if (localPart) return localPart.slice(0, 2).toUpperCase();
  }
  return "?";
};

export const getDisplayName = (user) => {
  if (!user) return "Unknown user";
  const first = (user.first_name || "").trim();
  const last = (user.last_name || "").trim();
  const full = `${first} ${last}`.trim();
  return full || user.username || user.email || "Unknown user";
};

// Human label for a role string coming off the backend. Accepts snake_case
// (e.g. "super_admin") and returns Title-Cased space-separated labels
// ("Super Admin"). Kept dumb on purpose — backend is the source of truth for
// what roles exist.
export const formatRoleLabel = (role) => {
  if (!role || typeof role !== "string") return "";
  return role
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

const formatLastLogin = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
};

const ProfilePopover = ({ user, loading, onLogout }) => {
  const displayName = getDisplayName(user);
  const initials = getUserInitials(user);
  const roleLabel = formatRoleLabel(user?.roles?.[0]);
  const lastLogin = formatLastLogin(user?.last_login);

  return (
    <ProfilePopoverContent role="menu" aria-label="User profile menu">
      <ProfilePopoverHeader>
        <ProfilePopoverAvatar aria-hidden="true">{initials}</ProfilePopoverAvatar>
        <ProfilePopoverIdentity>
          <ProfileName title={displayName}>{displayName}</ProfileName>
          {user?.email && (
            <ProfileEmail title={user.email}>{user.email}</ProfileEmail>
          )}
          {roleLabel && <ProfileRoleBadge>{roleLabel}</ProfileRoleBadge>}
        </ProfilePopoverIdentity>
      </ProfilePopoverHeader>

      {lastLogin && (
        <>
          <ProfileDivider />
          <ProfileMeta>Last signed in: {lastLogin}</ProfileMeta>
        </>
      )}

      <ProfileDivider />

      <ProfileActionButton
        type="button"
        onClick={onLogout}
        disabled={loading}
        aria-label="Sign out"
      >
        <SignOut size={16} weight="regular" />
        {loading ? "Signing out..." : "Sign out"}
      </ProfileActionButton>
    </ProfilePopoverContent>
  );
};

ProfilePopover.propTypes = {
  user: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    username: PropTypes.string,
    email: PropTypes.string,
    roles: PropTypes.arrayOf(PropTypes.string),
    last_login: PropTypes.string,
  }),
  loading: PropTypes.bool,
  onLogout: PropTypes.func.isRequired,
};

ProfilePopover.defaultProps = {
  user: null,
  loading: false,
};

export default ProfilePopover;
